import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from "reactstrap";
import IaTrend from "../components/IaTrend";

// Qué espera hacer el bot según el precio (statusBot.plan, armado en server/bot/plan.py).

const KIND = {
  buy:    { color: '#2dce89', icon: '🟢' },
  sell:   { color: '#fb6340', icon: '🔻' },
  target: { color: '#11cdef', icon: '🎯' },
  stop:   { color: '#f5365c', icon: '🛑' },
  high:   { color: '#8898aa', icon: '⬆' },
  low:    { color: '#8898aa', icon: '⬇' },
};

const STATE = {
  waiting:     { label: 'Vigilando', color: '#11cdef' },
  in_position: { label: 'En operación', color: '#2dce89' },
  warming:     { label: 'Preparando', color: '#8898aa' },
  regime_off:  { label: 'En pausa', color: '#fb6340' },
  blocked:     { label: 'Bloqueado', color: '#f5365c' },
};

const card = { background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '14px 16px' };
const title = { fontSize: 11, color: '#8898aa', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' };

const money = (v, d = 2) => (v === null || v === undefined) ? '—'
  : Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const pct = (a, b) => (a && b) ? (a - b) / b * 100 : null;
const signed = (v) => v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
const decimals = (p) => (p && p < 10 ? 4 : 2);

function usePrice(symbol) {
  const [price, setPrice] = useState(null);
  const [dir, setDir] = useState(null);
  const [live, setLive] = useState(false);
  const prev = useRef(null);
  useEffect(() => {
    if (!symbol) return undefined;
    const pair = `${symbol.toLowerCase().replace('usdt', '')}usdt`;
    let ws, timer, closed = false;
    const connect = () => {
      ws = new WebSocket(`wss://stream.binance.com:9443/ws/${pair}@ticker`);
      ws.onopen = () => setLive(true);
      ws.onmessage = (ev) => {
        const p = parseFloat(JSON.parse(ev.data).c);
        if (prev.current !== null) setDir(p > prev.current ? 'up' : p < prev.current ? 'down' : null);
        prev.current = p;
        setPrice(p);
      };
      ws.onclose = () => { setLive(false); if (!closed) timer = setTimeout(connect, 3000); };
    };
    connect();
    return () => { closed = true; clearTimeout(timer); if (ws) ws.close(); };
  }, [symbol]);
  return { price, dir, live };
}

// Escalera de precios con el precio actual en su lugar
const Ladder = ({ steps, price }) => {
  const rows = [...steps].filter(s => s.price).sort((a, b) => b.price - a.price);
  const at = rows.findIndex(s => s.price < price);
  const marker = { kind: 'now', price };
  const list = at === -1 ? [...rows, marker] : [...rows.slice(0, at), marker, ...rows.slice(at)];
  const d = decimals(price);
  return (
    <div>
      {list.map((s, i) => s.kind === 'now' ? (
        <div key="now" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
          <div style={{ flex: 1, height: 2, background: '#5e72e4' }} />
          <span style={{ fontSize: 12, fontWeight: 800, color: '#5e72e4', whiteSpace: 'nowrap' }}>PRECIO ACTUAL ${money(price, d)}</span>
          <div style={{ flex: 1, height: 2, background: '#5e72e4' }} />
        </div>
      ) : (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '28px 1fr auto 70px', alignItems: 'center', gap: 8,
          padding: '8px 10px', borderRadius: 8, marginBottom: 4,
          background: s.done ? 'rgba(45,206,137,0.07)' : '#f9f9fb',
          borderLeft: `4px solid ${(KIND[s.kind] || KIND.high).color}`, opacity: s.done ? 0.75 : 1,
        }}>
          <span style={{ fontSize: 15 }}>{s.done ? '✅' : (KIND[s.kind] || KIND.high).icon}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#32325d' }}>{s.label}{s.done ? ' (hecha)' : ''}</div>
            {s.note && <div style={{ fontSize: 11, color: '#8898aa' }}>{s.note}</div>}
          </div>
          <span style={{ fontSize: 15, fontWeight: 800, color: (KIND[s.kind] || KIND.high).color }}>${money(s.price, d)}</span>
          <span style={{ fontSize: 12, color: '#8898aa', textAlign: 'right' }}>{s.done ? '' : signed(pct(s.price, price))}</span>
        </div>
      ))}
    </div>
  );
};

const PlanBot = ({ symbol, statusBot }) => {
  const { price: wsPrice, dir, live } = usePrice(symbol);
  const plan = statusBot.plan;
  const price = wsPrice || plan.price;
  const rules = plan.rules || {};
  const st = STATE[plan.state] || STATE.waiting;
  const isShort = rules.side === 'short';
  const isGrid = rules.side === 'grid';
  const pos = plan.position;
  const symbolLabel = (symbol || '').replace('USDT', '');
  // Con el precio en vivo el avance hacia la señal se recalcula sin esperar al servidor
  const trigger = (plan.ladder || []).find(s => s.kind === (isShort ? 'sell' : 'buy') && /1\//.test(s.label));
  const ref = (plan.ladder || []).find(s => s.kind === (isShort ? 'low' : 'high'));
  let progress = plan.progressPct || 0;
  if (ref && price && plan.state === 'waiting') {
    const move = isShort ? pct(price, ref.price) : -pct(price, ref.price);
    progress = Math.max(0, Math.min(100, move / rules.triggerPct * 100));
  }
  // DCA Dual puede tener dos operaciones abiertas; los demás bots, una
  const positions = plan.positions || (pos ? [pos] : []);
  const pnlOf = p => (price ? (isShort ? p.invested - p.qty * price : p.qty * price - p.invested) : null);
  const holdOf = p => (p.minutesLeft !== null ? 100 - p.minutesLeft / (rules.maxHoldH * 60) * 100 : 0);

  return (
    <div style={{ padding: '0 8px' }}>
      {/* Precio y estrategia */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: live ? '#2dce89' : '#fb6340', display: 'inline-block' }} />
          <span style={{ fontSize: 11, color: '#8898aa' }}>{live ? 'En vivo' : 'Reconectando…'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#8898aa' }}>{symbolLabel}/USDT</span>
          <span style={{ fontSize: 26, fontWeight: 800, color: '#32325d' }}>${money(price, decimals(price))}</span>
          {dir === 'up' && <span style={{ fontSize: 18, color: '#2dce89' }}>▲</span>}
          {dir === 'down' && <span style={{ fontSize: 18, color: '#f5365c' }}>▼</span>}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: isShort ? '#fb6340' : '#2dce89',
            background: isShort ? 'rgba(251,99,64,0.1)' : 'rgba(45,206,137,0.1)', borderRadius: 6, padding: '3px 10px' }}>
            {rules.strategy} · {isGrid ? 'compra y vende entre líneas' : isShort ? 'vende subidas bruscas' : 'compra caídas bruscas'}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'white', background: st.color, borderRadius: 6, padding: '3px 10px' }}>{st.label}</span>
        </div>
      </div>

      {/* Qué está haciendo */}
      <div style={{ ...card, borderLeft: `5px solid ${st.color}`, marginBottom: 16 }}>
        <div style={title}>Qué está haciendo el bot</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#32325d', lineHeight: 1.4 }}>{plan.headline}</div>
        {plan.state === 'waiting' && trigger && !isGrid && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#8898aa', marginBottom: 4 }}>
              <span>{isShort ? 'Subida' : 'Caída'} actual hacia la señal ({rules.triggerPct}%)</span>
              <span><b style={{ color: '#32325d' }}>{progress.toFixed(0)}%</b> del camino · señal en ${money(trigger.price, decimals(price))}</span>
            </div>
            <div style={{ height: 10, background: '#e9ecef', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, background: isShort ? '#fb6340' : '#2dce89', transition: 'width 0.5s' }} />
            </div>
          </div>
        )}
      </div>

      <Row style={{ marginLeft: 0, marginRight: 0, gap: 12 }}>
        {/* Escalera */}
        <Col style={{ ...card, padding: '14px 16px' }}>
          <div style={title}>{isGrid ? 'Líneas más cercanas del grid' : positions.length > 1 ? 'Precios de la operación más antigua' : pos ? 'Precios de la operación abierta' : 'Qué haría si llega la señal'}</div>
          <Ladder steps={plan.ladder || []} price={price} />
          <div style={{ fontSize: 11, color: '#8898aa', marginTop: 8 }}>
            {isGrid
              ? `${rules.grids} grids geométricos entre $${money(rules.lower, decimals(price))} y $${money(rules.upper, decimals(price))}, paso ${rules.stepPct}% (${rules.netPct}% por vuelta después de comisión). Compra ${rules.lineUsdt} USDT en cada línea y la vende una línea más arriba. Lleva ${rules.cycles} vueltas (${rules.profit >= 0 ? '+' : ''}${money(rules.profit)} USDT).`
              : isShort
              ? `Vende de a ${rules.partUsdt} USDT (hasta ${rules.levels} partes, cada ${rules.spacingPct}% más arriba). Recompra todo a −${rules.takeProfitPct}% del promedio o a las ${rules.maxHoldH} h.`
              : `Compra de a ${rules.partUsdt} USDT (hasta ${rules.levels} partes, cada ${rules.spacingPct}% más abajo). Vende todo a +${rules.takeProfitPct}% del promedio o a las ${rules.maxHoldH} h.`}
          </div>
        </Col>

        {/* Condiciones / posición + IA */}
        <Col xs={12} md={4} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0 }}>
          {positions.length ? positions.map((p, i) => {
            const livePnl = pnlOf(p);
            return (
            <div key={i} style={card}>
              <div style={title}>{isGrid ? 'Moneda que tiene el grid' : positions.length > 1 ? `Operación ${i + 1} de ${positions.length}` : 'Operación abierta'}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: livePnl >= 0 ? '#2dce89' : '#f5365c' }}>
                {livePnl >= 0 ? '+' : ''}{money(livePnl)} USDT
              </div>
              <div style={{ fontSize: 12, color: '#8898aa', marginBottom: 10 }}>resultado si cerrara ahora (sin comisiones)</div>
              {[
                [isGrid ? 'Líneas con moneda' : isShort ? 'Ventas hechas' : 'Compras hechas', `${p.entries} de ${p.maxEntries}`],
                ['Precio promedio', `$${money(p.avgPrice, decimals(price))}`],
                [isShort ? 'Recibido' : 'Invertido', `${money(p.invested)} USDT`],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ color: '#8898aa' }}>{k}</span><b style={{ color: '#32325d' }}>{v}</b>
                </div>
              ))}
              {p.minutesLeft !== null && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: '#8898aa', marginBottom: 4 }}>
                    Cierre por tiempo en <b style={{ color: '#32325d' }}>{Math.floor(p.minutesLeft / 60)} h {p.minutesLeft % 60} min</b>
                  </div>
                  <div style={{ height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${holdOf(p)}%`, background: '#5e72e4' }} />
                  </div>
                </div>
              )}
            </div>
            );
          }) : (
            <div style={card}>
              <div style={title}>{isGrid ? 'Condiciones del grid' : `Condiciones para ${isShort ? 'vender' : 'comprar'}`}</div>
              {(plan.conditions || []).map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 14 }}>{c.ok ? '✅' : '⏳'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: '#32325d', fontWeight: 600 }}>{c.label}</div>
                    <div style={{ fontSize: 12, color: c.ok ? '#2dce89' : '#8898aa' }}>Ahora: {String(c.value)}</div>
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: '#8898aa', marginTop: 8 }}>
                {isGrid ? 'Opera mientras el precio está dentro del rango.' : 'Abre cuando se cumplen todas al cierre de una vela de 1 minuto.'}
              </div>
            </div>
          )}
          <IaTrend prediction={statusBot.prediction} />
        </Col>
      </Row>
    </div>
  );
};

export default PlanBot;
